import { Card, Text, View } from "@src/components";
import type { WasteSenseKpis } from "@visioni/a3s-reports/client-messages";

interface WeeklySummaryProps {
  kpis: WasteSenseKpis;
}

export interface KpiCardProps {
  label: string;
  value: string;
  helper?: string;
  /**
   * Padding override (px) applied to both axes. Defaults to the `Card`'s own
   * 18px. The hero's compact "Confirmed Detections" card passes a smaller value.
   */
  padding?: number;
  /** Horizontal alignment of the big value. Defaults to left. */
  valueAlign?: "left" | "right";
  /**
   * Grid tiles fill their row track (`flex:1`, `minWidth:160`) so they share
   * width evenly — the default. The hero card instead shrinks to fit its label,
   * so it passes `fill={false}`.
   */
  fill?: boolean;
  /**
   * Vertical gap between label / value / helper. Defaults to the `xs` (4px)
   * spacing token; the hero card passes `"none"` to sit them flush.
   */
  gap?: "none" | "xxs" | "xs" | "sm";
}

/**
 * A single KPI tile — uppercase label, large value, optional helper line.
 * Exported so the hero can render a matching "Confirmed Detections" card with
 * the same styling (with a few overrides: tighter padding, right-aligned value,
 * shrink-to-fit width).
 */
export function KpiCard({ label, value, helper, padding, valueAlign = "left", fill = true, gap = "xs" }: KpiCardProps) {
  return (
    <Card flex={fill ? 1 : undefined} minWidth={fill ? 160 : undefined} gap={gap} px={padding} py={padding}>
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      <Text textStyle="h2" color="text" textAlign={valueAlign}>
        {value}
      </Text>
      {helper ?
        <Text textStyle="xs/regular" color="textMuted">
          {helper}
        </Text>
      : null}
    </Card>
  );
}

/**
 * "Detections processed" block — KPI grid of media-volume numbers (clips +
 * snapshots). Numbers come pre-computed from the generator so the renderer never
 * disagrees with the email digest or the PDF. The confirmed-dump count is
 * surfaced separately in the hero, so it isn't repeated here.
 */
export function WeeklySummary({ kpis }: WeeklySummaryProps) {
  const fmt = (n: number) => n.toLocaleString();

  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Detections processed
      </Text>
      <View flexDirection="row" flexWrap="wrap" gap="md">
        <KpiCard label="Clips Recorded" value={fmt(kpis.clips_recorded)} helper="Total captured this week" />
        <KpiCard label="Snapshots Captured" value={fmt(kpis.snapshots_captured)} helper="Total captured this week" />
      </View>
    </View>
  );
}
